import { apiClient } from '../axios-instance'

export interface CommissionRule {
    id: number
    scope?: 'product' | 'category' | 'seller' | 'campaign' | 'global'
    product_id?: number // Legacy
    product_name?: string // Legacy
    category_id?: number
    category_name?: string
    products?: { id: number; name: string }[]
    seller_id?: number
    campaign_id?: number
    commission_type: 'percentage' | 'fixed'
    commission_value: number
    min_order_value?: number
    max_order_value?: number
    min_quantity?: number
    max_quantity?: number
    effective_from?: string
    effective_to?: string
    status: 'active' | 'inactive'
    priority: number
    created_at: string
    updated_at: string
    product?: {
        id: number
        name: string
    }
    category?: {
        id: number
        name: string
    }
}

export interface CommissionsResponse {
    success: boolean
    data: CommissionRule[]
    meta: {
        current_page: number
        per_page: number
        total: number
        last_page: number
    }
}

export interface CreateCommissionRequest {
    product_id?: number
    category_id?: number
    commission_type: 'percentage' | 'fixed'
    commission_value: number
    min_order_value?: number
    max_order_value?: number
    status?: 'active' | 'inactive'
    priority?: number
}

export interface UpdateCommissionRequest {
    product_id?: number
    category_id?: number
    commission_type?: 'percentage' | 'fixed'
    commission_value?: number
    min_order_value?: number
    max_order_value?: number
    status?: 'active' | 'inactive'
    priority?: number
}

class CommissionsService {
    async getCommissions(
        page: number = 1,
        perPage: number = 15,
        filters?: {
            status?: string
            product_id?: number
            category_id?: number
            commission_type?: string
            search?: string
        }
    ): Promise<CommissionsResponse> {
        const params: Record<string, string> = {
            page: page.toString(),
            per_page: perPage.toString(),
        }

        if (filters?.status) params.status = filters.status
        if (filters?.product_id) params.product_id = filters.product_id.toString()
        if (filters?.category_id) params.category_id = filters.category_id.toString()
        if (filters?.commission_type) params.commission_type = filters.commission_type
        if (filters?.search) params.search = filters.search

        const queryString = new URLSearchParams(params).toString()
        const response = await apiClient.get(`/admin/commissions?${queryString}`)
        return response.data
    }

    async getCommissionById(id: number): Promise<{ success: boolean; data: CommissionRule }> {
        const response = await apiClient.get(`/admin/commissions/${id}`)
        return response.data
    }

    async createCommission(data: CreateCommissionRequest): Promise<{ success: boolean; data: CommissionRule }> {
        const response = await apiClient.post('/admin/commissions', data)
        return response.data
    }

    async updateCommission(id: number, data: UpdateCommissionRequest): Promise<{ success: boolean; data: CommissionRule }> {
        const response = await apiClient.put(`/admin/commissions/${id}`, data)
        return response.data
    }

    async deleteCommission(id: number): Promise<{ success: boolean }> {
        const response = await apiClient.delete(`/admin/commissions/${id}`)
        return response.data
    }

    async calculateCommission(data: {
        product_id?: number
        category_id?: number
        order_value: number
    }): Promise<{
        success: boolean
        data: {
            commission_amount: number
            commission_type: string | null
            commission_value: number | null
            rule_id: number | null
            order_value: number
            message: string
        }
    }> {
        const response = await apiClient.post('/admin/commissions/calculate', data)
        return response.data
    }
}

export const commissionsService = new CommissionsService()
